/* eslint-disable max-len */
import React, { useState, useMemo, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Search, Info, Clock, Timer, BarChart3, Building2, Camera,
} from 'lucide-react';
import { UserContext } from '../../contexts/User';
import Avatar from '../../components/avatar';
import ActivityBar from '../../components/activityBar';
import { convertSecToTimeFormat } from '../../utils/time';
import { calculateTimeSummary } from '../../utils/activity';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

const STATUS_PALETTE = {
  good: { bg: '#e6f4e6', icon: '#53A551', label: 'Available' },
  warn: { bg: '#fdecd7', icon: '#E89B2C', label: 'Not Confirmed' },
  bad: { bg: '#fbe6e0', icon: '#CC4525', label: 'Not Available' },
};

function engagementColor(value) {
  if (value >= 70) return 'text-shoot-success';
  if (value >= 40) return 'text-[#F19235]';
  return 'text-[#b94f3c]';
}

function StatusIcon({
  kindLabel, status, icon: Icon,
}) {
  const p = STATUS_PALETTE[status] ?? STATUS_PALETTE.good;
  return (
    <Tooltip arrow placement="top" title={`${kindLabel} – ${p.label}`}>
      <span
        className="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition hover:scale-105"
        style={{ backgroundColor: p.bg }}
      >
        <Icon size={16} style={{ color: p.icon }} />
      </span>
    </Tooltip>
  );
}

StatusIcon.propTypes = {
  kindLabel: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
};

function MetricInline({
  icon: Icon, label, value, valueClass,
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <Icon size={14} className="text-ink-500 flex-shrink-0" />
      <span className="text-ink-500">
        {label}
        :
      </span>
      <span className={`font-semibold tabular-nums ${valueClass ?? 'text-ink-900'}`}>
        {value}
      </span>
    </div>
  );
}

MetricInline.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueClass: PropTypes.string,
};

function TeamActivityCard({
  team, timezone, curDate,
}) {
  const { teamId } = useParams();
  const { redirect } = useContext(UserContext);
  const [searchStr, setSearchStr] = useState('');
  const searchRes = useMemo(() => (
    team.teamMates?.filter((mate) => {
      const userName = `${mate.member?.user.firstName ?? ''} ${mate.member?.user.lastName ?? ''}`;
      return userName.toLowerCase().includes(searchStr.toLowerCase());
    })
  ), [searchStr, team.teamMates]);

  // Driven entirely by the page-level date picker
  const selectedDate = useMemo(() => moment(curDate).toDate(), [curDate]);

  const handleSearch = (event) => {
    setSearchStr(event.target.value);
  };

  return (
    <div className={`rounded-xl bg-white border border-surface-2 ${softShadow} overflow-hidden`}>
      <div className="px-5 pt-5 pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-surface-1">
        <h3 className="text-base font-semibold text-ink-900 inline-flex items-center gap-1.5">
          Team Members Activity
          <Tooltip
            arrow
            placement="right"
            title="Track the participation and contributions of team members to assess their
              involvement and productivity in various projects."
          >
            <span className="inline-flex items-center justify-center text-ink-500 cursor-help">
              <Info size={14} />
            </span>
          </Tooltip>
        </h3>
        <div className="relative w-full sm:w-64">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search members"
            onChange={handleSearch}
            className="w-full pl-9 pr-3 py-2 border border-rule rounded-lg text-sm bg-white text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[800px] team-activity-rows">
          {searchRes?.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-ink-500">
              No members match the search.
            </div>
          ) : (
            searchRes?.map((mate) => {
              const userName = mate.member?.user.firstName && mate.member?.user.lastName
                ? `${mate.member?.user.firstName} ${mate.member?.user.lastName}`
                : mate.member?.user.email;
              const { totalSeconds = 0, activeSeconds = 0 } = calculateTimeSummary(mate.member?.activity);
              const totalActivityPercentage = Math.round((activeSeconds * 100) / totalSeconds || 0);
              const tzToUse = timezone !== 'Members timezone' ? timezone : mate.member?.user.timezone;
              const dayStart = moment.tz(selectedDate, tzToUse).startOf('day').utc();
              const dayEnd = moment.tz(selectedDate, tzToUse).endOf('day').utc();
              const daySnapshots = (mate.member?.activity || []).filter((snap) => {
                const ts = moment.utc(snap?.timeSlot);
                return ts.isSameOrAfter(dayStart) && ts.isSameOrBefore(dayEnd);
              });
              const locationStatus = mate.member?.locationViolations?.length > 0 ? 'bad' : 'good';
              // eslint-disable-next-line no-nested-ternary
              const workHoursStatus = totalActivityPercentage >= 51
                ? 'good'
                : totalActivityPercentage >= 21
                  ? 'warn'
                  : 'bad';
              const auditStatus = mate.member?.webcamViolations?.length > 0 ? 'bad' : 'good';
              const isManager = mate.role === 'Manager';

              return (
                <div
                  key={mate.memberId}
                  className="team-activity-row px-5 py-4 hover:bg-surface-page transition"
                >
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-4">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Avatar
                        src={mate.member?.user.profilePicture}
                        name={(mate.member && mate.member.user.firstName ? mate.member.user.firstName[0] : 'N')
                          + (mate.member && mate.member.user.lastName ? mate.member.user.lastName[0] : 'A')}
                        width={40}
                        height={40}
                        color="#3d5a8a"
                        backgroundColor="#e8eef7"
                      />
                      <div className="text-sm">
                        <Link
                          to={`/profile/${mate.memberId}?from=/members/team-engagement`}
                          className="font-semibold text-brand-500 hover:underline"
                          style={{ textDecoration: 'none' }}
                        >
                          {userName}
                        </Link>
                        {isManager && (
                          <>
                            <span className="text-ink-500 mx-1.5">•</span>
                            <span className="text-ink-500">Leader</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 ml-2">
                        <StatusIcon kindLabel="Work Location" status={locationStatus} icon={Building2} />
                        <StatusIcon kindLabel="Work Hours" status={workHoursStatus} icon={Clock} />
                        <StatusIcon kindLabel="Audit" status={auditStatus} icon={Camera} />
                      </div>
                    </div>
                    <div className="flex items-center gap-5 flex-wrap ml-auto">
                      <MetricInline
                        icon={Clock}
                        label="Total Hours"
                        value={convertSecToTimeFormat(totalSeconds)}
                      />
                      <MetricInline
                        icon={Clock}
                        label="Manual Hours"
                        value={convertSecToTimeFormat(mate.member?.manualTimeEntry?.[0]?.durationSeconds ?? 0)}
                      />
                      <MetricInline
                        icon={Timer}
                        label="Quality Hours"
                        value={convertSecToTimeFormat(activeSeconds)}
                      />
                      <MetricInline
                        icon={BarChart3}
                        label="Engagement Score"
                        value={`${totalActivityPercentage ?? 0}%`}
                        valueClass={engagementColor(totalActivityPercentage)}
                      />
                      <button
                        type="button"
                        onClick={() => { redirect(`/members/team-engagement/${teamId}/member/${mate.memberId}`); }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-700 transition"
                      >
                        View Report
                      </button>
                    </div>
                  </div>
                  <div className="pb-3">
                    <ActivityBar
                      snapshots={daySnapshots}
                      curDate={moment(selectedDate).startOf('day').format('YYYY-MM-DD HH:mm:ss')}
                      timezone={tzToUse}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

TeamActivityCard.propTypes = {
  team: PropTypes.shape({
    teamMates: PropTypes.arrayOf(
      PropTypes.shape({
        memberId: PropTypes.number.isRequired,
        member: PropTypes.shape({
          user: PropTypes.shape({
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired,
            profilePicture: PropTypes.string.isRequired,
          }),
          activity: PropTypes.arrayOf(PropTypes.shape({})),
          dailyActivity: PropTypes.arrayOf(
            PropTypes.shape({
              totalSeconds: PropTypes.number,
              activeSeconds: PropTypes.number,
            }),
          ),
          locationViolations: PropTypes.arrayOf(PropTypes.shape({})),
          webcamViolations: PropTypes.arrayOf(PropTypes.shape({})),
        }),
      }),
    ),
  }),
  timezone: PropTypes.string.isRequired,
  curDate: PropTypes.string.isRequired,
};

export default TeamActivityCard;
